import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { app, getAuth, getWatchingHook, getUser, getHook, getDocu, logOut } from "./firebaseConfig";
import Loading from '../components/Loading';
import { Button, notification, Result } from 'antd';
import { useTranslation } from 'react-i18next';
import { normalizeMapBounds, normalizeMapCenter, normalizeMapZoom } from '../constants/maps';
import { isAlertCancelled, isAlertViewed, isAlertVisible } from '../constants/alerts';

const Auth = React.createContext({ name: '', user: null, menu: null });

const AuthContext = ({ children }) => {
    const audio = useRef(new Audio('https://firebasestorage.googleapis.com/v0/b/api-kaps.appspot.com/o/bell2.mp3?alt=media&token=d3fdd7a4-8149-4c74-8c11-871b98748f35')).current;
    // const nav = useNavigate();
    const loc = useLocation();
    const { t } = useTranslation();
    const [user, setUsuario] = useState(null);
    // const [viewedAlerts, setViewedAlerts] = useState([]); // Estado para almacenar las alertas vistas
    // Inicializamos directamente leyendo del localStorage.
    // useRef no provoca re-renders, por lo que es perfecto para silenciar notificaciones ya emitidas sin reiniciar el listener.
    const viewedAlertsRef = useRef(JSON.parse(localStorage.getItem('viewedAlerts')) || []);
    const [audioAllowed, setAudioAllowed] = useState(false);
    const isMounted = useRef(true); // Referencias para identificar esta pestaña y controlar el ciclo de vida

    // ESTADOS DE CONTROL
    const [checkingSession, setCheckingSession] = useState(true); // Estado de bloqueo. Mientras sea true, mostramos Loading. Esto evita que se vea el panel si la sesión no es válida.
    const [sessionConflict, setSessionConflict] = useState(false); // Nuevo: Para mostrar la pantalla de bloqueo

    /* const services = [
        'AHP',
        'REP',
        'TGD',
        'REMOTO2',
        'CASERCC',
        'DUCATI',
        'KYMCO',
        'AUT',
        'PUS'
    ]; */

    // Habilitar audio con interacción (Click o Movimiento)
    const enableAudio = useCallback(() => {
        // El "secreto" para Chrome: reproducir un sonido silencioso o muy corto tras el click
        audio.play()
            .then(() => {
                audio.pause();
                audio.currentTime = 0;
                setAudioAllowed(true);
                console.log("✅ Audio desbloqueado por el navegador");

                // Limpiamos los eventos una vez desbloqueado
                document.removeEventListener('click', enableAudio);
                document.removeEventListener('keydown', enableAudio);
                document.removeEventListener('touchstart', enableAudio);
            })
            .catch(err => console.warn("⚠️ Esperando interacción del usuario real...", err));
    }, [audio]);


    // Listener inicial para activar audio
    useEffect(() => {
        isMounted.current = true;
        // Cargamos lo que haya en storage (útil si se recarga la página, no tanto al cambiar de usuario)
        // const stored = JSON.parse(localStorage.getItem('viewedAlerts')) || [];
        // setViewedAlerts(stored);
        // Si el audio no está permitido, escuchamos
        if (!audioAllowed) {
            document.addEventListener('mousemove', enableAudio);
            document.addEventListener('click', enableAudio);
            document.addEventListener('keydown', enableAudio);
            document.addEventListener('touchstart', enableAudio);
        }

        return () => {
            isMounted.current = false;
            document.removeEventListener('mousemove', enableAudio);
            document.removeEventListener('click', enableAudio);
            document.removeEventListener('keydown', enableAudio);
            document.removeEventListener('touchstart', enableAudio);
        };
    }, [enableAudio, audioAllowed]); // Dependencia audioAllowed clave para reiniciar tras logout


    // 1. MONITOR DE AUTENTICACIÓN (onAuthStateChanged). Logica ppal de autenticacion y sesion
    useEffect(() => {
        const au = getAuth(app);
        // Listener de Firebase Auth
        const unsubscribeAuth = au.onAuthStateChanged(async (firebaseUser) => {
            if (firebaseUser) {
                // 1. Usuario logueado en Firebase. Ahora verificamos DB.
                if (isMounted.current) {
                    try {
                        const dbUser = await getUser(firebaseUser.email);
                        if (!dbUser) {
                            // Usuario no existe en DB -> Fuera
                            logOut();
                            return;
                        }

                        // --- VALIDACIÓN ESTRICTA DE SESIÓN ---
                        const dbSessionID = dbUser.sessionIDs ? Number(dbUser.sessionIDs[0]) : null;
                        const localSessionID = sessionStorage.getItem('sessionID') ? Number(sessionStorage.getItem('sessionID')) : null;
                        // Si NO coinciden y NO estamos en la pantalla de login intentando entrar...
                        if (loc.pathname !== '/login' && loc.pathname !== '/') {
                            if (!localSessionID || !dbSessionID || localSessionID !== dbSessionID) {
                                // Activamos el modo "Conflicto" que congela la pantalla.No hacemos logOut() inmediato para evitar parpadeos.
                                setSessionConflict(true);
                                setCheckingSession(false);
                                return;
                            }
                        }

                        // Si llegamos aquí, la sesión es válida. Cargamos config.
                        const service = await getDocu({ coll: 'services', docu: dbUser.service });
                        firebaseUser.rol = dbUser.rol;
                        firebaseUser.service = dbUser.service;
                        firebaseUser.configs = {
                            timezone: service?.timezone || 'Europe/Madrid',
                            mapCenter: normalizeMapCenter(service?.mapCenter),
                            mapZoom: normalizeMapZoom(service?.mapZoom),
                            customMap: normalizeMapBounds(service?.customMap),
                            mapType: service?.mapType || 'roadmap'
                        };
                        setUsuario(firebaseUser);
                        setSessionConflict(false); // es ok
                        setCheckingSession(false); // ABRIMOS LA PUERTA
                    } catch (err) {
                        console.error("Error obteniendo usuario", err);
                        logOut();
                    }
                }
            } else {
                // Usuario no logueado
                setUsuario(null);
                setCheckingSession(false); // Dejamos de cargar (mostrará Login o redirigirá)
            }
        });
        return () => unsubscribeAuth();
    }, [loc.pathname]); // Se ejecuta al cambiar de ruta para verificar siempre


    // 2. MONITOR DE SESIÓN ÚNICA (Watcher de Session para expulsion en tiempo real)
    // Este useEffect vigila si OTRA pestaña cambia el ID en la DB mientras tú estás dentro.
    useEffect(() => {
        // Solo activamos esto si ya tenemos un usuario validado
        if (user && user.email && !sessionConflict) {
            const unsubscribeSession = getWatchingHook({ coll: 'users', alertId: user.email },
                (querySnapshot) => {
                    if (!isMounted.current) return;
                    const d = querySnapshot.data();
                    if (!d) return;

                    const dbSessionId = d.sessionIDs ? Number(d.sessionIDs[0]) : null;
                    const localSessionId = sessionStorage.getItem('sessionID') ? Number(sessionStorage.getItem('sessionID')) : null;

                    // Si estamos navegando normalmente (no login) y los IDs difieren
                    if (loc.pathname !== '/login' && loc.pathname !== '/') {
                        if (dbSessionId && localSessionId && dbSessionId !== localSessionId) {
                            console.warn("Detectado inicio de sesión en otra pestaña.");
                            // Bloqueamos la pantalla inmediatamente
                            setUsuario(null); // Esto apaga el monitor de alertas al instante
                            setSessionConflict(true);
                        }
                    }
                }
            );
            return () => unsubscribeSession();
        }
    }, [user, loc.pathname, sessionConflict]);


    // 3. MONITOR DE ALERTAS
    useEffect(() => {
        // la campana de alertas sonará cuando hacemos login y hay alertas sin ser vistas (no hay campo viewed) 
        // o cuando se crea una alerta nueva en la coll alerts, el resto de cambios no provocan que suene la campana. 
        // Añadimos un escuchador a la collection alerts para que permita que suene la campanita si se cumplen las condiciones. 
        // Ademas, se debe mostrar una notificacion por cada nueva alert

        // 1. Verificación estricta para no saltar en pestañas viejas
        if (!user || !user.service || sessionConflict) return;

        // 2. No ejecutar en rutas de login
        if (loc.pathname === '/' || loc.pathname === '/login') return;

        const roles = user.rol || [];
        const isOperatorOrAdmin = roles.some(r => r.includes('OPERATOR') || r.includes('ADMIN'));

        if (user.service && isOperatorOrAdmin) {
            // Usamos un pequeño delay de 500ms para asegurar que el DOM está listo
            const timer = setTimeout(() => {
                console.log("Monitor de alertas activo para:", user.service);

                const unsubscribeAlerts = getHook({ coll: 'alerts', order: 'alertId', service: user.service, lim: null },
                    (snap) => {
                        if (!isMounted.current) return;
                        snap.docChanges().forEach((change) => {
                            if (change.type === "added" || change.type === "modified") {
                                // console.log("New alert: ", change.doc.data().alertId);
                                const data = change.doc.data();
                                const alertId = String(data.alertId); // Forzamos String para evitar errores de comparación
                                if (isAlertVisible(data) && !isAlertViewed(data) && !isAlertCancelled(data)) {
                                    // 1. Revisamos nuestro historial local de sesión (Ref)
                                    // Convertimos a String para asegurar que .includes funcione bien
                                    const alreadyViewedInSession = viewedAlertsRef.current.map(String).includes(alertId);

                                    // 2. Revisamos el "Bloqueo Global" en LocalStorage (para coordinar pestañas y frenar bucles)
                                    // Buscamos si alguien (esta u otra pestaña) notificó esto hace menos de 10 segundos
                                    const lockKey = `notif_lock_${alertId}`;
                                    const lastNotifTime = localStorage.getItem(lockKey);
                                    const now = Date.now();
                                    const isRecentlyNotified = lastNotifTime && (now - Number(lastNotifTime) < 10000); // 10 segundos de "silencio"

                                    if (!alreadyViewedInSession && !isRecentlyNotified) {

                                        console.log("🔔 Nueva alerta válida y no notificada recientemente:", alertId);

                                        // A. BLOQUEAMOS INMEDIATAMENTE: Escribimos en LS para que nadie más (ni yo mismo en bucle) la toque
                                        localStorage.setItem(lockKey, now.toString());

                                        // B. Actualizamos Referencia Local
                                        viewedAlertsRef.current = [...viewedAlertsRef.current, alertId];
                                        localStorage.setItem('viewedAlerts', JSON.stringify(viewedAlertsRef.current));

                                        // C. Reproducir Audio (Simple, sin lógica compleja de desbloqueo aquí para evitar re-renders)
                                        if (audio) {
                                            // Intentamos reproducir. Si falla por falta de interacción, solo hacemos log.
                                            // No cambiamos estados aquí para no reiniciar el useEffect.
                                            audio.play().catch(e => console.warn("Audio autoplay bloqueado", e));
                                        }

                                        // Mostrar notificación durante 5 segundos si aun no se ha mostrado
                                        notification.open({
                                            message: t('MSG_NEW_ALERT_TITLE'),
                                            description: t('MSG_NEW_ALERT_DESCRIPTION'),
                                        });
                                    }
                                }
                            }
                            // if (change.type === "modified") {
                            //     console.log("Modified alert: ", change.doc.data());
                            // }
                            // if (change.type === "removed") {
                            //     console.log("Removed alert: ", change.doc.data());
                            // }
                        });
                    },
                    (error) => {
                        console.log(`kk de la vaca asturiana, ${error}`)
                    }
                );
                return () => unsubscribeAlerts();
            }, 1000);

            return () => clearTimeout(timer);
        }
    }, [user, loc.pathname, sessionConflict]);


    // 4. DETECTOR DE NUEVA VERSIÓN
    /**
     * ¿Cómo funciona este flujo?
     * Ejecución: Lo ejecuta el navegador del cliente dentro del contexto de autenticación.
     * Momento: Se dispara justo después de que el "Portero" (tu validación de sesión) le deja pasar. También se vuelve a chequear de forma silenciosa cada vez que el usuario navega por el menú (loc.pathname).
     * Acción: * Si la versión en Firebase es "2.1.0" y tu código dice "2.1.0", no pasa nada.
     * Si mañana subes la 2.1.1 a Firebase, el usuario verá aparecer la notificación de repente. Al hacer clic en "Actualizar ahora", el comando window.location.reload(true) fuerza al navegador a ignorar la caché y descargar el JavaScript nuevo.
    */
    useEffect(() => {
        // Solo comprobamos si el usuario está dentro y no hay conflicto de sesión
        if (user && !sessionConflict) {
            const currentVersion = "2.1.2"; // <--- AQUÍ pones la versión actual de tu código. La misma que en el footer del Login.js
            const checkVersion = async () => {
                try {
                    // Suponiendo que tienes una función getDocu o similar
                    const appData = await getDocu({ coll: 'config', docu: 'console_version' });

                    if (appData && appData.version !== currentVersion) {
                        notification.warning({
                            message: 'Nueva versión disponible (v' + appData.version + ')',
                            description: 'Se han aplicado cambios importantes. Por favor, pulsa el botón para actualizar la aplicación.',
                            duration: 0, // No se cierra sola
                            btn: (
                                <Button type="primary" size="small" onClick={() => window.location.reload(true)}>
                                    Actualizar ahora
                                </Button>
                            ),
                            key: 'versionUpdate' // Evita notificaciones duplicadas
                        });
                    }
                } catch (err) {
                    console.log("Error verificando versión", err);
                }
            };

            checkVersion();
        }
    }, [user, loc.pathname]);


    // --- RENDERIZADO CONDICIONAL: ESTO ES EL PORTERO ---
    // 1. Si estamos validando, Spinner.
    if (checkingSession) {
        return <Loading />;
    }

    // 2. Si hay CONFLICTO DE SESIÓN (Caso Pestaña Vieja)
    // Mostramos esto en lugar de redirigir o mostrar la app.
    if (sessionConflict) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#f0f2f5' }}>
                <Result
                    status="warning"
                    title={t('SESSION_EXPIRED_TITLE') || "Sesión caducada"}
                    subTitle={t('SESSION_EXPIRED_MSG') || "Has iniciado sesión en otra pestaña o navegador. Para continuar aquí, por favor inicia sesión de nuevo."}
                    extra={
                        <Button type="primary" key="console" onClick={() => {
                            logOut(); // Ahora sí hacemos el logout limpio y redirigimos
                        }}>
                            {t('BUTTON_NAV_LOGIN') || "Ir al Login"}
                        </Button>
                    }
                />
            </div>
        );
    }

    // 3. Si todo está bien, mostramos la App.
    return (
        <Auth.Provider value={{ user }} >
            {children}
        </Auth.Provider>
    );
};


function useFirebaseAuth() {
    const context = React.useContext(Auth);
    if (context === undefined) {
        console.log("useFirebaseAuth must be used within a FirebaseAuthProvider")
        throw new Error(
            "useFirebaseAuth must be used within a FirebaseAuthProvider"
        );
    }
    return context.user;
}

export { Auth, AuthContext, useFirebaseAuth };
